import { useState, useEffect, useCallback } from "react";
import { message } from "antd";
import { ServiceWorkerMessage, SyncQueueStatus } from "../types/storage.types";

interface UseServiceWorkerReturn {
  swController: ServiceWorker | null;
  isReady: boolean;
  sendMessage: (message: ServiceWorkerMessage) => void;
  setSyncQueueStatus: React.Dispatch<React.SetStateAction<SyncQueueStatus>>;
}

export const useServiceWorker = (): UseServiceWorkerReturn => {
  const [swController, setSwController] = useState<ServiceWorker | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [syncQueueStatus, setSyncQueueStatus] = useState<SyncQueueStatus>({
    pendingPosts: 0,
    lastSyncStatus: null,
  });

  const handleSwMessage = useCallback((event: MessageEvent) => {
    if (!event.data || !event.data.type) return;

    console.log("🎯 Mensaje SW recibido:", event.data.type);

    switch (event.data.type) {
      case "SYNC_COMPLETED":
        message.success(
          `Sincronización: ${event.data.successCount} exitosa(s), ${event.data.failedCount} fallida(s)`
        );
        setSyncQueueStatus((prev) => ({
          ...prev,
          lastSyncStatus: event.data.failedCount > 0 ? "failed" : "success",
          lastSyncError:
            event.data.failedCount > 0
              ? "Algunas sincronizaciones fallaron"
              : undefined,
        }));
        break;

      case "PENDING_ITEMS_RESULT":
        setSyncQueueStatus((prev) => ({
          ...prev,
          pendingPosts: event.data.hasPending ? prev.pendingPosts : 0,
        }));
        break;

      default:
        console.log("🔄 Mensaje SW no manejado:", event.data.type);
        break;
    }
  }, []);

  const sendMessage = useCallback(
    (messageData: ServiceWorkerMessage) => {
      if (!swController) {
        console.warn("⚠️ Service Worker no disponible para enviar mensaje");
        return;
      }

      try {
        swController.postMessage(messageData);
      } catch (error) {
        console.error("❌ Error enviando mensaje al SW:", error);
        message.error("Error comunicándose con el Service Worker");
      }
    },
    [swController]
  );

  useEffect(() => {
    if (!("serviceWorker" in navigator)) {
      console.warn("⚠️ Service Workers no soportados");
      return;
    }

    const initializeServiceWorker = async () => {
      try {
        const registration = await navigator.serviceWorker.ready;
        console.log("✅ Service Worker listo");

        setSwController(registration.active);
        setIsReady(true);

        navigator.serviceWorker.addEventListener("message", handleSwMessage);
      } catch (error) {
        console.warn("⚠️ Error con Service Worker:", error);
        setIsReady(false);
      }
    };

    initializeServiceWorker();

    return () => {
      navigator.serviceWorker.removeEventListener("message", handleSwMessage);
    };
  }, [handleSwMessage]);

  return {
    swController,
    isReady,
    sendMessage,
    setSyncQueueStatus: setSyncQueueStatus,
  };
};
